import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/auth';

const api = axios.create({ baseURL: '/api', timeout: 20000 });
const authApi = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function SetAlertModal({ symbol, name, currentPrice, onClose, onSaved, defaultCondition }) {
  const [targetPrice, setTargetPrice] = useState(currentPrice ? currentPrice.toFixed(2) : '');
  const [condition, setCondition] = useState(defaultCondition || (currentPrice ? 'above' : 'above'));
  const [levels, setLevels] = useState(null);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  // Auto-set condition based on price vs current (only when no default provided)
  useEffect(() => {
    if (defaultCondition) return;
    const p = parseFloat(targetPrice);
    if (!isNaN(p) && currentPrice) {
      setCondition(p > currentPrice ? 'above' : 'below');
    }
  }, [targetPrice, currentPrice, defaultCondition]);

  // Click-outside to close
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const loadLevels = async () => {
    setLevelsLoading(true);
    try {
      const { data } = await api.get(`/alerts/support-resistance/${symbol}`, authApi());
      setLevels(data);
    } catch { setLevels(null); }
    finally { setLevelsLoading(false); }
  };

  useEffect(() => { loadLevels(); }, [symbol]);

  const save = async () => {
    const p = parseFloat(targetPrice);
    if (isNaN(p) || p <= 0) return setError('Please enter a valid price');
    setSaving(true);
    setError(null);
    try {
      await api.post('/alerts', { symbol, name, targetPrice: p, condition }, authApi());
      // Request browser notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        await Notification.requestPermission();
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save alert');
    } finally { setSaving(false); }
  };

  const condColor = condition === 'above' ? 'var(--green)' : 'var(--red)';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div ref={ref} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 28, width: '100%', maxWidth: 420,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Set Price Alert</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {symbol} · Current: <span style={{ color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>${currentPrice?.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {/* Target price input */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
            Alert Price
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }}>$</span>
            <input
              type="number"
              step="0.01"
              value={targetPrice}
              onChange={e => setTargetPrice(e.target.value)}
              className="input"
              style={{ paddingLeft: 24, fontFamily: 'JetBrains Mono, monospace', fontSize: 15 }}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Condition display */}
        <div style={{
          background: condition === 'above' ? 'var(--green-dim)' : 'var(--red-dim)',
          border: `1px solid ${condColor}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 20,
          fontSize: 13, color: condColor, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>{condition === 'above' ? '↑' : '↓'}</span>
          Notify when price goes <strong>{condition}</strong> ${parseFloat(targetPrice) > 0 ? parseFloat(targetPrice).toFixed(2) : '—'}
        </div>

        {/* Support / Resistance quick-set */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Quick Set — Support &amp; Resistance
          </div>
          {levelsLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Calculating levels…</div>}
          {levels && !levelsLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Resistance */}
              {levels.resistance.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 700, marginBottom: 4 }}>↑ Resistance</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {levels.resistance.map(p => (
                      <button key={p} onClick={() => { setTargetPrice(p.toFixed(2)); setCondition('above'); }} style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid var(--red)',
                        background: 'var(--red-dim)', color: 'var(--red)', cursor: 'pointer',
                        fontSize: 12, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600,
                      }}>${p.toFixed(2)}</button>
                    ))}
                  </div>
                </div>
              )}
              {/* Support */}
              {levels.support.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, marginBottom: 4 }}>↓ Support</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {levels.support.map(p => (
                      <button key={p} onClick={() => { setTargetPrice(p.toFixed(2)); setCondition('below'); }} style={{
                        padding: '4px 10px', borderRadius: 6, border: '1px solid var(--green)',
                        background: 'var(--green-dim)', color: 'var(--green)', cursor: 'pointer',
                        fontSize: 12, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600,
                      }}>${p.toFixed(2)}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'Saving…' : '🔔 Set Alert'}
          </button>
        </div>
      </div>
    </div>
  );
}
